import { Injectable } from '@angular/core';
import { LoadingController, LoadingOptions, ToastController, AlertController, AlertOptions, IonicSafeString } from '@ionic/angular';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Events } from 'src/providers/events/events';

@Injectable()
export class CommonUtils {
  constructor(
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private alertController: AlertController,
    private events: Events,
  ) {}

  async showLoading(params?: LoadingOptions): Promise<HTMLIonLoadingElement> {
    const options: LoadingOptions = {
      message: new TranslationPipe().transform('Loading...'),
      spinner: 'circles',
      mode: 'ios',
      ...params,
    };
    const loading = await this.loadingCtrl.create(options);

    loading.present();
    return loading;
  }

  async showInfo(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: 'toast-info',
    });
    toast.present();
  }

  async showError(message: string, duration = 5000, showLogOutPrompt = true): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: !showLogOutPrompt ? message : new IonicSafeString(`${new TranslationPipe().transform(message)}<br><br>${new TranslationPipe().transform('Having persistent issues? ')}<a href=\"#\" id=\"logout-link\" style=\"color: white; text-decoration: underline\">${new TranslationPipe().transform('Try logging out')}</a>`),
      duration,
      cssClass: 'toast-error'
    });
    await toast.present();
    if (showLogOutPrompt) {
      setTimeout(() => {
        const toastEl = document.querySelector('ion-toast.toast-error');
        if (toastEl) {
          const logoutLink = toastEl.shadowRoot ? toastEl.shadowRoot.querySelector('#logout-link') : toastEl.querySelector('#logout-link');
          if (logoutLink) {
            logoutLink.addEventListener('click', (e) => {
              e.preventDefault();
              this.logOut();
              toast.dismiss();
            });
          }
        }
      }, 500);
    }
  }

  async showSuccess(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: 'toast-success',
    });
    toast.present();
  }

  async showConfirmAlert(options: AlertOptions): Promise<HTMLIonAlertElement> {
    const alert = await this.alertController.create({
      header: new TranslationPipe().transform('Confirm'),
      message: new TranslationPipe().transform('Are you sure you want to do this?'),
      mode: 'ios',
      buttons: [
        {
          text: new TranslationPipe().transform('Go Back'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: new TranslationPipe().transform('Confirm'),
          role: 'confirm',
        },
      ],
      ...options,
    });
    alert.present();
    return alert;
  }

  async logOut(): Promise<void> {
    this.events.publish('auth:logout');
  }

  formatGuestReservationDetails(details: any): string {
    try {
      if (!details) return '';

      const hasSomeDetails = details?.total_guests > 0 || details?.total_adults > 0 || details?.total_children > 0 || details?.total_pets > 0 || details?.total_infants > 0;

      if (!hasSomeDetails) return '';

      const translationPipe = new TranslationPipe();
      const parts: string[] = [];

      if (details?.total_guests > 0) {
        const guestWord = translationPipe.transform(details.total_guests === 1 ? 'guest' : 'guests');
        parts.push(`${details.total_guests} ${guestWord}`);
      }

      const guestDetails: string[] = [];

      if (details?.total_adults > 0) {
        const adultWord = translationPipe.transform(details.total_adults === 1 ? 'adult' : 'adults');
        guestDetails.push(`${details.total_adults} ${adultWord}`);
      }

      if (details?.total_children > 0) {
        const childrenWord = translationPipe.transform('children');
        guestDetails.push(`${details.total_children} ${childrenWord}`);
      }

      if (details?.total_pets > 0) {
        const petWord = translationPipe.transform(details.total_pets === 1 ? 'pet' : 'pets');
        guestDetails.push(`${details.total_pets} ${petWord}`);
      }

      if (details?.total_infants > 0) {
        const infantWord = translationPipe.transform(details.total_infants === 1 ? 'infant' : 'infants');
        guestDetails.push(`${details.total_infants} ${infantWord}`);
      }

      if (guestDetails?.length > 0) {
        parts.push(guestDetails.join(' | '));
      }

      const guestsLabel = translationPipe.transform('Guests');
      return parts?.length > 0 ? `${guestsLabel}: ${parts?.join(' | ')}` : '';
    } catch (err) {
      return '';
    }
  }

  parsePhoneNumber(value: string): string {
    const parsedValue = value.replace(/[^\d+]/g, '').replace('+', '');
    return `+${  parsedValue}`;
  }

}
